import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CREATE_jwel, CREATE_jwel1, tenantName } from "../../Config/Config";
import axios from "axios";

const C = {
    purple: "#4c3494",
    purple2: "#7c5cbf",
    purple3: "#ede9ff",
    teal: "#0d9488",
    teal2: "#ccfbf1",
    pink: "#ec4899",
    pink2: "#fce7f3",
    amber: "#f59e0b",
    amber2: "#fef3c7",
    blue: "#3b82f6",
    blue2: "#dbeafe",
    indigo: "#6366f1",
    indigo2: "#e0e7ff",
    green: "#16a34a",
    green2: "#dcfce7",
    whatsapp: "#25D366",
    text: "#1e1b4b",
    textSub: "#6b7280",
};

const TENANT_NAME = tenantName;
const API_HEADERS = {
    "Content-Type": "application/json",
    tenantName: TENANT_NAME,
};

/* ── buildUpdateString ─────────────────────────────────────────────────────── */
/**
 * Same InsertCredit-style dynamic SQL string builder as CashFreeDetails,
 * just pointed at the WHATSAPP_* columns instead of APP_*.
 * WHATSAPP_SCHEME is a `bit` column, so it's written unquoted as 1/0 rather
 * than through the quoted-string path the rest of the fields use.
 */
function buildUpdateString(form, whatsappEnabled) {
    const fields = [
        { col: "WHATSAPP_KEY", val: form.whatsappKey },
        { col: "WHATSAPP_SENDER", val: form.senderId },
        { col: "WHATSAPP_CAMPAIGNID", val: form.campaignId },
        { col: "WHATSAPP_SALEINVOICE", val: form.saleInvoice },
        { col: "WHATSAPP_CUSTADVANCE", val: form.custAdvance },
        { col: "WHATSAPP_ORDERADV", val: form.orderAdvance },
        { col: "WHATSAPP_CUSTRECEIPT", val: form.custReceipt },
        { col: "WHATSAPP_ESTIMATION", val: form.estimation },
        { col: "WHATSAPP_APPINVOICE", val: form.approvalInvoice },
        { col: "WHATSAPP_URDGOLD", val: form.urdGold },
        { col: "WHATSAPP_SALERETURN", val: form.saleReturn },
        { col: "WHATSAPP_B2BINVOICE", val: form.b2bInvoice },
        { col: "WHATSAPP_DELIVERYCHALLAN", val: form.deliveryChallan },
        { col: "WHATSAPP_OPT_TEMPLETE", val: form.otpTemplate },
        { col: "WHATSAPP_BRANCHISSUE", val: form.branchIssue },
        { col: "WHATSAPP_BIRTHDAY", val: form.birthday },
        { col: "WHATSAPP_ANNIVERSARY", val: form.anniversary },
        { col: "WHATSAPP_OFFER1", val: form.offer1 },
        { col: "WHATSAPP_OFFER2", val: form.offer2 },
        { col: "WHATSAPP_NEWSCHEME", val: form.newScheme },
        { col: "WHATSAPP_SCHEME_TEMPLETE", val: form.schemeReceipt },
        { col: "WHATSAPP_SCHEMEJOIN", val: form.schemeJoin },
        { col: "WHATSAPP_SCHEMESETTLE", val: form.schemeSettle },
        { col: "WHATSAPP_PREVILAGEPOINTS", val: form.previlagePoints },
    ];
    const quoted = fields
        .map(({ col, val }) => `${col}='${(val || "").replace(/'/g, "''")}'`)
        .join(",");
    // bit column — unquoted 1/0, not a quoted string
    return `${quoted},WHATSAPP_SCHEME=${whatsappEnabled ? 1 : 0}`;
}

/* ── ShimmerLine ───────────────────────────────────────────────────────────── */
const ShimmerLine = ({
    colors = [C.purple, C.blue, C.teal, C.pink, C.amber],
    height = 3,
}) => (
    <motion.div
        style={{
            height,
            borderRadius: height,
            background: `linear-gradient(90deg, ${colors.join(", ")}, ${colors[0]})`,
            backgroundSize: "300% 100%",
        }}
        animate={{ backgroundPosition: ["0% 0%", "100% 0%", "0% 0%"] }}
        transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
    />
);

/* ── SectionDivider ────────────────────────────────────────────────────────── */
const SectionDivider = ({ icon, title, accent }) => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            margin: "4px 0 14px",
        }}
    >
        <motion.div
            animate={{ rotate: [0, 8, -8, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            style={{
                width: 28,
                height: 28,
                borderRadius: 7,
                flexShrink: 0,
                background: `linear-gradient(135deg,${accent},${accent}bb)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                boxShadow: `0 3px 10px ${accent}44`,
            }}
        >
            {icon}
        </motion.div>
        <span
            style={{
                fontSize: "clamp(12px,0.7vw,14px)",
                fontWeight: 800,
                color: accent,
                textTransform: "uppercase",
                letterSpacing: "1.4px",
                whiteSpace: "nowrap",
            }}
        >
            {title}
        </span>
        <div
            style={{
                flex: 1,
                height: 1,
                background: `linear-gradient(90deg,${accent}44,transparent)`,
            }}
        />
        <motion.div
            animate={{ opacity: [1, 0.3, 1], scale: [1, 1.5, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: accent,
                boxShadow: `0 0 6px ${accent}`,
                flexShrink: 0,
            }}
        />
    </div>
);

/* ── VALIDATORS ────────────────────────────────────────────────────────────── */
// Only the three "main" fields are mandatory, same way appBaseUrl/appClientId/
// appClientSecret/appWebhook were mandatory in CashFreeDetails. The 21
// template fields underneath are optional — a firm may not use every
// template — so they render without the red-asterisk / error treatment.
const VALIDATORS = {
    whatsappKey: {
        filter: () => true,
        error: (v) => (v.trim() ? "" : "Required"),
    },
    senderId: {
        filter: () => true,
        error: (v) => (v.trim() ? "" : "Required"),
    },
    campaignId: {
        filter: () => true,
        error: (v) => (v.trim() ? "" : "Required"),
    },
};

/* ── Field ─────────────────────────────────────────────────────────────────── */
const Field = ({
    fieldKey,
    label,
    placeholder,
    value,
    onChange,
    icon,
    accent = C.purple,
    accBg = C.purple3,
    required = true,
    disabled = false,
}) => {
    const [focused, setFocused] = useState(false);
    const [touched, setTouched] = useState(false);

    const validator = required ? VALIDATORS[fieldKey] : null;

    const handleChange = (e) => {
        const raw = e.target.value;
        if (validator && !validator.filter(raw)) return;
        onChange(e);
    };

    const handleBlur = () => {
        setFocused(false);
        setTouched(true);
    };

    const errorMsg = touched && validator ? validator.error(value) : "";
    const isEmpty = value === "" || value == null;
    const isValid = touched && !isEmpty && !errorMsg;

    const borderColor = disabled
        ? "rgba(0,0,0,0.08)"
        : errorMsg
            ? "#ef4444"
            : isValid
                ? "#22c55e"
                : focused
                    ? accent
                    : "rgba(0,0,0,0.10)";
    const shadowColor = errorMsg
        ? "rgba(239,68,68,0.12)"
        : isValid
            ? "rgba(34,197,94,0.12)"
            : `${accent}18`;

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: 5,
                minWidth: 0,
                opacity: disabled ? 0.55 : 1,
                transition: "opacity 0.2s",
            }}
        >
            <label
                style={{
                    fontSize: "clamp(11px,0.62vw,13px)",
                    fontWeight: 800,
                    color: disabled
                        ? C.textSub
                        : errorMsg
                            ? "#ef4444"
                            : isValid
                                ? "#16a34a"
                                : focused
                                    ? accent
                                    : C.textSub,
                    letterSpacing: "1px",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    transition: "color 0.2s",
                }}
            >
                <span style={{ fontSize: 11 }}>{icon}</span>
                {label}
                {required && (
                    <span
                        style={{
                            color: isValid ? "#22c55e" : errorMsg ? "#ef4444" : "#f59e0b",
                            fontSize: 13,
                            lineHeight: 1,
                        }}
                    >
                        *
                    </span>
                )}
                {required && touched && !isEmpty && !disabled && (
                    <motion.span
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        style={{ marginLeft: "auto", fontSize: 11 }}
                    >
                        {isValid ? "✅" : "❌"}
                    </motion.span>
                )}
            </label>

            <motion.div
                animate={focused && !disabled ? { scale: 1.01 } : { scale: 1 }}
                transition={{ duration: 0.15 }}
                style={{ position: "relative" }}
            >
                <input
                    value={value}
                    onChange={handleChange}
                    placeholder={placeholder}
                    disabled={disabled}
                    onFocus={() => setFocused(true)}
                    onBlur={handleBlur}
                    style={{
                        width: "100%",
                        background: disabled
                            ? "rgba(241,245,249,0.7)"
                            : focused
                                ? accBg
                                : "rgba(255,255,255,0.75)",
                        border: `1.5px solid ${borderColor}`,
                        borderRadius: 8,
                        padding: "8px 12px",
                        color: disabled ? C.textSub : C.text,
                        fontSize: 13,
                        fontWeight: 600,
                        outline: "none",
                        boxSizing: "border-box",
                        transition: "all 0.2s",
                        cursor: disabled ? "not-allowed" : "text",
                        boxShadow:
                            focused && !disabled
                                ? `0 0 0 3px ${shadowColor}`
                                : "0 1px 3px rgba(0,0,0,0.06)",
                    }}
                />
                <AnimatePresence>
                    {focused && !disabled && (
                        <motion.div
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            exit={{ scaleX: 0 }}
                            style={{
                                position: "absolute",
                                bottom: 0,
                                left: "10%",
                                right: "10%",
                                height: 2,
                                background: `linear-gradient(90deg,transparent,${borderColor},transparent)`,
                                borderRadius: "0 0 8px 8px",
                            }}
                        />
                    )}
                </AnimatePresence>
            </motion.div>

            <AnimatePresence>
                {errorMsg && !disabled && (
                    <motion.span
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#ef4444",
                            letterSpacing: "0.4px",
                            paddingLeft: 2,
                        }}
                    >
                        ⚠ {errorMsg}
                    </motion.span>
                )}
            </AnimatePresence>
        </div>
    );
};

/* ── Row ───────────────────────────────────────────────────────────────────── */
const Row = ({ children, cols }) => (
    <div
        style={{
            display: "grid",
            gridTemplateColumns: `repeat(${cols || children.length},1fr)`,
            gap: 12,
        }}
    >
        {children}
    </div>
);

/* ── StatusBadge ───────────────────────────────────────────────────────────── */
const StatusBadge = ({ status, message }) => {
    const config = {
        loading: { bg: "#dbeafe", color: "#1d4ed8", icon: "⏳", text: "Saving..." },
        success: {
            bg: "#d1fae5",
            color: "#065f46",
            icon: "✅",
            text: message || "Saved!",
        },
        error: {
            bg: "#fee2e2",
            color: "#991b1b",
            icon: "❌",
            text: message || "Save failed",
        },
    }[status];
    if (!config) return null;
    return (
        <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: 8,
                background: config.bg,
                color: config.color,
                fontSize: 12,
                fontWeight: 700,
            }}
        >
            <span>{config.icon}</span>
            {config.text}
        </motion.div>
    );
};

/* ── EnableToggle ──────────────────────────────────────────────────────────── */
/**
 * The header checkbox. Checked ⇒ WHATSAPP_SCHEME = true and every field
 * below becomes editable. Unchecked ⇒ WHATSAPP_SCHEME = false and every
 * field below is shown but disabled, same as the spec asked for.
 */
const EnableToggle = ({ checked, onChange }) => (
    <motion.label
        whileTap={{ scale: 0.97 }}
        style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: "pointer",
            padding: "6px 12px 6px 6px",
            borderRadius: 999,
            background: checked ? C.green2 : "rgba(0,0,0,0.05)",
            border: `1.5px solid ${checked ? C.green : "rgba(0,0,0,0.12)"}`,
            transition: "all 0.2s",
        }}
    >
        <input
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            style={{ display: "none" }}
        />
        <span
            style={{
                width: 34,
                height: 19,
                borderRadius: 999,
                background: checked ? C.green : "#cbd5e1",
                position: "relative",
                flexShrink: 0,
                transition: "background 0.2s",
            }}
        >
            <motion.span
                animate={{ left: checked ? 17 : 2 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
                style={{
                    position: "absolute",
                    top: 2,
                    width: 15,
                    height: 15,
                    borderRadius: "50%",
                    background: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
            />
        </span>
        <span
            style={{
                fontSize: 12,
                fontWeight: 800,
                color: checked ? "#166534" : C.textSub,
                whiteSpace: "nowrap",
            }}
        >
            {checked ? "Templates Enabled" : "Enable Templates"}
        </span>
    </motion.label>
);

/* ══════════════════════════════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════════════════════════════ */
export default function WhatsAppTemplates() {
    const [apiStatus, setApiStatus] = useState(null);
    const [apiMessage, setApiMessage] = useState("");
    const [fetchLoading, setFetchLoading] = useState(true);
    const [originalFirmName, setOriginalFirmName] = useState("");
    const [whatsappEnabled, setWhatsappEnabled] = useState(false);

    const [form, setForm] = useState({
        whatsappKey: "",
        senderId: "",
        campaignId: "",
        saleInvoice: "",
        custAdvance: "",
        orderAdvance: "",
        custReceipt: "",
        estimation: "",
        approvalInvoice: "",
        urdGold: "",
        saleReturn: "",
        b2bInvoice: "",
        deliveryChallan: "",
        otpTemplate: "",
        branchIssue: "",
        birthday: "",
        anniversary: "",
        offer1: "",
        offer2: "",
        newScheme: "",
        schemeReceipt: "",
        schemeJoin: "",
        schemeSettle: "",
        previlagePoints: "",
    });

    /* ── GET ── */
    useEffect(() => {
        const fetchData = async () => {
            setFetchLoading(true);
            try {
                const res = await fetch(
                    `${CREATE_jwel}/api/ERP/GetDataFromGivenTableName?tableName=Firm_Configure`,
                    { method: "GET", headers: API_HEADERS },
                );
                const data = await res.json();
                const d = Array.isArray(data) ? data[0] : data;

                setOriginalFirmName(d.FIRMNAME || "");
                if (d) {
                    setWhatsappEnabled(!!d.WHATSAPP_SCHEME);
                    setForm({
                        whatsappKey: d.WHATSAPP_KEY || "",
                        senderId: d.WHATSAPP_SENDER || "",
                        campaignId: d.WHATSAPP_CAMPAIGNID || "",
                        saleInvoice: d.WHATSAPP_SALEINVOICE || "",
                        custAdvance: d.WHATSAPP_CUSTADVANCE || "",
                        orderAdvance: d.WHATSAPP_ORDERADV || "",
                        custReceipt: d.WHATSAPP_CUSTRECEIPT || "",
                        estimation: d.WHATSAPP_ESTIMATION || "",
                        approvalInvoice: d.WHATSAPP_APPINVOICE || "",
                        urdGold: d.WHATSAPP_URDGOLD || "",
                        saleReturn: d.WHATSAPP_SALERETURN || "",
                        b2bInvoice: d.WHATSAPP_B2BINVOICE || "",
                        deliveryChallan: d.WHATSAPP_DELIVERYCHALLAN || "",
                        otpTemplate: d.WHATSAPP_OPT_TEMPLETE || "",
                        branchIssue: d.WHATSAPP_BRANCHISSUE || "",
                        birthday: d.WHATSAPP_BIRTHDAY || "",
                        anniversary: d.WHATSAPP_ANNIVERSARY || "",
                        offer1: d.WHATSAPP_OFFER1 || "",
                        offer2: d.WHATSAPP_OFFER2 || "",
                        newScheme: d.WHATSAPP_NEWSCHEME || "",
                        schemeReceipt: d.WHATSAPP_SCHEME_TEMPLETE || "",
                        schemeJoin: d.WHATSAPP_SCHEMEJOIN || "",
                        schemeSettle: d.WHATSAPP_SCHEMESETTLE || "",
                        previlagePoints: d.WHATSAPP_PREVILAGEPOINTS || "",
                    });
                }
            } catch (err) {
                console.error("Failed to load WhatsApp template data:", err);
            } finally {
                setFetchLoading(false);
            }
        };
        fetchData();
    }, []);

    const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    /* ── validate required fields before save (only when enabled) ── */
    const isFormValid = () =>
        !whatsappEnabled ||
        Object.keys(VALIDATORS).every((k) => !VALIDATORS[k].error(form[k]));

    /* ── SAVE ── */
    const handleSave = async () => {
        if (!isFormValid()) {
            setApiStatus("error");
            setApiMessage("Please fill all required fields");
            setTimeout(() => setApiStatus(null), 3000);
            return;
        }
        try {
            setApiStatus("loading");
            await axios.post(
                `${CREATE_jwel1}/api/Master/CommonUpdateWithWhere`,
                {
                    tableName: "Firm_Configure",
                    updateString: buildUpdateString(form, whatsappEnabled),
                    whereString: `FIRMNAME='${originalFirmName}'`,
                },
                { headers: { tenantName: TENANT_NAME } },
            );
            setApiStatus("success");
            setApiMessage("WhatsApp templates saved!");
            setTimeout(() => setApiStatus(null), 3000);
        } catch {
            setApiStatus("error");
            setApiMessage("Update failed");
            setTimeout(() => setApiStatus(null), 4000);
        }
    };

    /* SVG patterns */
    const checkPatternSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='60' height='60'><text x='10' y='42' font-size='32' fill='rgba(99,102,241,0.07)' font-family='Arial'>✓</text></svg>`;
    const checkPatternUrl = `url("data:image/svg+xml,${encodeURIComponent(checkPatternSvg)}")`;
    const stripesSvg = `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'>${[0, 16, 32, 48, 64, 80, 96, 112].map((i) => `<line x1='${i}' y1='0' x2='${i + 20}' y2='120' stroke='rgba(99,102,241,0.06)' stroke-width='8'/>`).join("")}</svg>`;
    const stripesUrl = `url("data:image/svg+xml,${encodeURIComponent(stripesSvg)}")`;

    // Every field below the toggle shares the same disabled state.
    const fieldsDisabled = !whatsappEnabled;

    const templateFields = [
        { key: "saleInvoice", label: "Sale Invoice", icon: "🧾" },
        { key: "custAdvance", label: "Customer Advance", icon: "💰" },
        { key: "orderAdvance", label: "Order Advance", icon: "📦" },
        { key: "custReceipt", label: "Customer Receipt", icon: "🧷" },
        { key: "estimation", label: "Estimation", icon: "📐" },
        { key: "approvalInvoice", label: "Approval Invoice", icon: "✅" },
        { key: "saleReturn", label: "Sale Return", icon: "↩️" },
        { key: "b2bInvoice", label: "B2B Invoice", icon: "🏢" },
        { key: "deliveryChallan", label: "Delivery Challan", icon: "🚚" },
        { key: "otpTemplate", label: "OTP Template", icon: "🔑" },
        { key: "branchIssue", label: "Branch Issue", icon: "🏬" },
        { key: "birthday", label: "Birthday", icon: "🎂" },
        { key: "anniversary", label: "Anniversary", icon: "💍" },
        { key: "offer1", label: "Offer 1", icon: "🎁" },
        { key: "offer2", label: "Offer 2", icon: "🎉" },
        { key: "previlagePoints", label: "Previlage Points", icon: "⭐" },
    ];

    // chunk into pairs so they render exactly like the two-up Client ID /
    // Client Secret row in CashFreeDetails
    const templateRows = [];
    for (let i = 0; i < templateFields.length; i += 2) {
        templateRows.push(templateFields.slice(i, i + 2));
    }

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "28px 16px",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <style>{`
        input::placeholder { color:#b0afc8 !important; }
        * { box-sizing:border-box; }
        ::-webkit-scrollbar { width:5px; }
        ::-webkit-scrollbar-thumb { background:rgba(99,102,241,0.35); border-radius:3px; }
        @keyframes orbFloat { 0%,100%{transform:translate(0,0) scale(1);} 50%{transform:translate(20px,-18px) scale(1.08);} }
        .lgt-orb { position:fixed; border-radius:50%; pointer-events:none; filter:blur(55px); }
      `}</style>

            {/* Orbs */}
            {[
                {
                    w: 320,
                    h: 320,
                    bg: "rgba(37,211,102,0.18)",
                    t: "-60px",
                    l: "-60px",
                    dur: "14s",
                },
                {
                    w: 260,
                    h: 260,
                    bg: "rgba(13,148,136,0.15)",
                    b: "-40px",
                    r: "-40px",
                    dur: "18s",
                    rev: true,
                },
                {
                    w: 200,
                    h: 200,
                    bg: "rgba(99,102,241,0.12)",
                    t: "40%",
                    l: "5%",
                    dur: "12s",
                    delay: "2s",
                },
                {
                    w: 180,
                    h: 180,
                    bg: "rgba(236,72,153,0.12)",
                    t: "20%",
                    r: "8%",
                    dur: "16s",
                    delay: "1s",
                },
            ].map((o, i) => (
                <div
                    key={i}
                    className="lgt-orb"
                    style={{
                        width: o.w,
                        height: o.h,
                        background: o.bg,
                        top: o.t,
                        bottom: o.b,
                        left: o.l,
                        right: o.r,
                        animation: `orbFloat ${o.dur} ease-in-out infinite${o.rev ? " reverse" : ""}${o.delay ? ` ${o.delay}` : ""}`,
                    }}
                />
            ))}

            {/* Card */}
            <motion.div
                initial={{ opacity: 0, y: 36, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                style={{
                    width: "100%",
                    maxWidth: 780,
                    borderRadius: 22,
                    background:
                        "linear-gradient(160deg,rgba(255,255,255,0.95) 0%,rgba(243,240,255,0.97) 40%,rgba(236,253,245,0.96) 75%,rgba(255,251,235,0.97) 100%)",
                    border: "1px solid rgba(99,102,241,0.18)",
                    boxShadow:
                        "0 20px 60px rgba(99,102,241,0.14),0 4px 16px rgba(0,0,0,0.07)",
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                {/* Patterns */}
                <div
                    style={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: checkPatternUrl,
                        backgroundRepeat: "repeat",
                        pointerEvents: "none",
                        zIndex: 0,
                    }}
                />
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: 140,
                        height: 140,
                        backgroundImage: stripesUrl,
                        backgroundRepeat: "repeat",
                        opacity: 0.6,
                        pointerEvents: "none",
                        zIndex: 0,
                        borderRadius: "0 22px 0 0",
                    }}
                />
                <div
                    style={{
                        position: "absolute",
                        width: 200,
                        height: 200,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(rgba(37,211,102,0.16),transparent 70%)",
                        top: -50,
                        right: 80,
                        pointerEvents: "none",
                        zIndex: 0,
                    }}
                />
                <div
                    style={{
                        position: "absolute",
                        width: 160,
                        height: 160,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(rgba(13,148,136,0.13),transparent 70%)",
                        bottom: -40,
                        left: 60,
                        pointerEvents: "none",
                        zIndex: 0,
                    }}
                />

                {/* Header */}
                <div
                    style={{
                        position: "relative",
                        zIndex: 1,
                        padding: "18px 24px 16px",
                        background:
                            "linear-gradient(90deg,rgba(37,211,102,0.10) 0%,rgba(13,148,136,0.07) 50%,rgba(99,102,241,0.06) 100%)",
                        borderBottom: "1px solid rgba(99,102,241,0.12)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 10,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <motion.div
                            animate={{ rotate: [0, 360] }}
                            transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
                            style={{
                                width: 44,
                                height: 44,
                                borderRadius: 12,
                                flexShrink: 0,
                                background: `linear-gradient(135deg,${C.whatsapp},${C.teal},${C.indigo})`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 21,
                                boxShadow: `0 6px 20px ${C.teal}44`,
                            }}
                        >
                            💬
                        </motion.div>
                        <div>
                            <div
                                style={{
                                    fontSize: 20,
                                    fontWeight: 900,
                                    letterSpacing: "-0.4px",
                                    background: `linear-gradient(90deg,${C.teal} 0%,${C.whatsapp} 50%,${C.indigo} 100%)`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                }}
                            >
                                WhatsApp SMS Templates
                            </div>
                            <div style={{ fontSize: 11, color: C.textSub, marginTop: 2 }}>
                                Manage your WhatsApp messaging templates and settings
                            </div>
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <EnableToggle checked={whatsappEnabled} onChange={setWhatsappEnabled} />
                        <AnimatePresence>
                            {apiStatus && (
                                <StatusBadge status={apiStatus} message={apiMessage} />
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                <ShimmerLine
                    colors={[C.whatsapp, C.teal, C.indigo, C.purple, C.pink, C.whatsapp]}
                />

                {/* Form Body */}
                <div
                    style={{
                        padding: "20px 24px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 18,
                        position: "relative",
                        zIndex: 1,
                        maxHeight: "62vh",
                        overflowY: "auto",
                    }}
                >
                    {/* Loading overlay */}
                    <AnimatePresence>
                        {fetchLoading && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 10,
                                    background: "rgba(255,255,255,0.75)",
                                    backdropFilter: "blur(3px)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 8,
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 10,
                                    }}
                                >
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{
                                            duration: 1,
                                            repeat: Infinity,
                                            ease: "linear",
                                        }}
                                        style={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: "50%",
                                            border: `3px solid ${C.teal2}`,
                                            borderTop: `3px solid ${C.teal}`,
                                        }}
                                    />
                                    <span
                                        style={{ fontSize: 12, fontWeight: 700, color: C.teal }}
                                    >
                                        Loading WhatsApp templates...
                                    </span>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* MAIN fields (WhatsApp Key / Sender ID / Campaign ID) — no
              subheading, sits directly under the header + toggle, same as
              CashFreeDetails puts its fields straight under "Gateway
              Settings" with no extra wrapper section above it. */}
                    <motion.div
                        initial={{ opacity: 0, x: -18 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.08, duration: 0.4 }}
                        style={{ display: "flex", flexDirection: "column", gap: 12 }}
                    >
                        <Field
                            fieldKey="whatsappKey"
                            label="WhatsApp Key"
                            icon="🔐"
                            placeholder="Enter your WhatsApp API key"
                            value={form.whatsappKey}
                            onChange={set("whatsappKey")}
                            accent={C.whatsapp}
                            accBg={C.green2}
                            disabled={fieldsDisabled}
                        />
                        <Row cols={2}>
                            <Field
                                fieldKey="senderId"
                                label="Sender ID"
                                icon="📤"
                                placeholder="Sender ID"
                                value={form.senderId}
                                onChange={set("senderId")}
                                accent={C.teal}
                                accBg={C.teal2}
                                disabled={fieldsDisabled}
                            />
                            <Field
                                fieldKey="campaignId"
                                label="Campaign ID"
                                icon="🆔"
                                placeholder="Campaign ID"
                                value={form.campaignId}
                                onChange={set("campaignId")}
                                accent={C.blue}
                                accBg={C.blue2}
                                disabled={fieldsDisabled}
                            />
                        </Row>
                    </motion.div>

                    {/* TEMPLATES section */}
                    <motion.div
                        initial={{ opacity: 0, x: -18 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.14, duration: 0.4 }}
                    >
                        <SectionDivider icon="📨" title="Templates" accent={C.indigo} />
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {templateRows.map((pair, i) => (
                                <Row cols={2} key={i}>
                                    {pair.map((f) => (
                                        <Field
                                            key={f.key}
                                            fieldKey={f.key}
                                            label={f.label}
                                            icon={f.icon}
                                            placeholder={`${f.label} template`}
                                            value={form[f.key]}
                                            onChange={set(f.key)}
                                            accent={C.purple}
                                            accBg={C.purple3}
                                            required={false}
                                            disabled={fieldsDisabled}
                                        />
                                    ))}
                                </Row>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Footer */}
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                    style={{
                        position: "relative",
                        zIndex: 1,
                        padding: "14px 24px",
                        borderTop: "1px solid rgba(99,102,241,0.12)",
                        background:
                            "linear-gradient(90deg,rgba(13,148,136,0.07) 0%,rgba(99,102,241,0.06) 50%,rgba(37,211,102,0.06) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                    }}
                >
                    <motion.button
                        onClick={handleSave}
                        disabled={apiStatus === "loading"}
                        whileHover={
                            apiStatus !== "loading"
                                ? { y: -3, boxShadow: `0 10px 26px ${C.teal}44` }
                                : {}
                        }
                        whileTap={apiStatus !== "loading" ? { scale: 0.96 } : {}}
                        style={{
                            padding: "9px 26px",
                            borderRadius: 9,
                            border: "none",
                            background:
                                apiStatus === "loading"
                                    ? "#94a3b8"
                                    : `linear-gradient(135deg,#0a7a74,${C.teal})`,
                            color: "#fff",
                            fontWeight: 800,
                            fontSize: 13,
                            cursor: apiStatus === "loading" ? "not-allowed" : "pointer",
                            boxShadow: `0 4px 14px ${C.teal}33`,
                            letterSpacing: "0.5px",
                            position: "relative",
                            overflow: "hidden",
                            transition: "background 0.2s",
                        }}
                    >
                        <AnimatePresence mode="wait">
                            {apiStatus === "loading" ? (
                                <motion.span
                                    key="loading"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                >
                                    ⏳ Saving...
                                </motion.span>
                            ) : (
                                <motion.span
                                    key="save"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                >
                                    💾 Save
                                </motion.span>
                            )}
                        </AnimatePresence>
                    </motion.button>
                </motion.div>

                <ShimmerLine
                    colors={[C.whatsapp, C.teal, C.indigo, C.purple, C.pink, C.whatsapp]}
                />
            </motion.div>

            {/* Toast */}
            <AnimatePresence>
                {(apiStatus === "success" || apiStatus === "error") && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.92 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 30 }}
                        style={{
                            position: "fixed",
                            bottom: 28,
                            right: 28,
                            zIndex: 9999,
                            background:
                                apiStatus === "success"
                                    ? `linear-gradient(135deg,#0a7a74,${C.teal})`
                                    : "linear-gradient(135deg,#b91c1c,#ef4444)",
                            borderRadius: 12,
                            padding: "12px 20px",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            boxShadow:
                                apiStatus === "success"
                                    ? `0 12px 36px ${C.teal}44`
                                    : "0 12px 36px rgba(239,68,68,0.4)",
                            border: `1px solid ${apiStatus === "success" ? C.teal : "#f87171"}77`,
                        }}
                    >
                        <motion.span
                            animate={{ rotate: [0, 15, -15, 0] }}
                            transition={{ duration: 0.5 }}
                            style={{ fontSize: 18 }}
                        >
                            {apiStatus === "success" ? "✅" : "❌"}
                        </motion.span>
                        <div>
                            <div style={{ color: "#fff", fontWeight: 800, fontSize: 13 }}>
                                {apiStatus === "success"
                                    ? "WhatsApp Templates Saved!"
                                    : "Save Failed"}
                            </div>
                            <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 11 }}>
                                {apiMessage ||
                                    (apiStatus === "success"
                                        ? "All changes have been applied."
                                        : "Please check your connection.")}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}